import React from 'react';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
} from '@/webapi/use-experience-api';
import { ChangeScreenshot } from '@/utils/change-screenshot';
import { CatalogApp } from '@/webapi/use-widget-catalog-api';
import {
  CheckoutWidgetChange,
  CompoundChange,
  HtmlMutationKind,
} from '@/pkg/sdk';
import { conditional } from '@/utils/conditional';
import { SimpleActionTitle } from './simple-action-title';
import { MoveActionTitle } from './move-action-title';
import { WidgetActionTitle } from './widget-action-title';

interface TitleFactoryProps {
  change: EditorDeclarativeBlock;
  ordinal: number;
  appsCatalog: CatalogApp[];
  screenshot: ChangeScreenshot;
}

export function TitleFactory({
  change,
  ordinal,
  appsCatalog,
  screenshot,
}: TitleFactoryProps): JSX.Element {
  if (
    change.editorKind === EditorChangeKind.EDIT_COMPOUND ||
    change.editorKind === EditorChangeKind.VISUAL_EDIT
  ) {
    return (
      <SimpleActionTitle
        prefix="Editing"
        change={change}
        screenshot={screenshot}
      />
    );
  }

  if (change.editorKind === EditorChangeKind.HIDE_COMPONENT) {
    return (
      <SimpleActionTitle
        prefix="Hiding"
        change={change}
        screenshot={screenshot}
      />
    );
  }

  if (
    change.editorKind === EditorChangeKind.GLOBAL_CSS ||
    change.editorKind === EditorChangeKind.GLOBAL_JS
  ) {
    return (
      <>
        Adding{` `}
        <b>
          {change.editorKind === EditorChangeKind.GLOBAL_CSS
            ? `Global CSS Stylesheet`
            : `Global Javascript`}
        </b>
      </>
    );
  }

  const isAiEditingChange =
    change?.editorKind === EditorChangeKind.NEW_COMPONENT &&
    (change?.block?.value as CompoundChange)?.isAiComponent &&
    (change?.block?.value as CompoundChange)?.selectedAgent?.id ===
      `section-modifier`;

  if (isAiEditingChange) {
    return (
      <SimpleActionTitle
        prefix="AI Editing of"
        change={change}
        screenshot={screenshot}
      />
    );
  }

  if (change.editorKind === EditorChangeKind.NEW_COMPONENT) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const htmlKind = change?.block?.value?.htmlKind as HtmlMutationKind;

    const prefix = conditional(
      `Adding`,
      (change.block.kind === `checkoutWidget` &&
        (change.block.value as CheckoutWidgetChange)?.op === `replace`) ||
        htmlKind === `replace`,
      `Replacing`,
    );

    const directive = conditional(
      `WITH`,
      change.block.kind === `checkoutWidget` &&
        (change.block.value as CheckoutWidgetChange)?.op !== `replace`,
      `INTO`,
      change.block.kind === `checkoutWidget` &&
        (change.block.value as CheckoutWidgetChange)?.op === `replace`,
      `WITHIN`,
      htmlKind === `appendAfter`,
      `AFTER`,
      htmlKind === `appendBefore`,
      `BEFORE`,
    );

    return (
      <WidgetActionTitle
        prefix={prefix}
        directive={directive}
        change={change}
        screenshot={screenshot}
        appsCatalog={appsCatalog}
      />
    );
  }

  if (change.editorKind === EditorChangeKind.MOVE_COMPONENT) {
    return <MoveActionTitle change={change} screenshot={screenshot} />;
  }

  if (change.editorKind === EditorChangeKind.FAKE_CLICK) {
    if (change?.hint) {
      return <span>{change.hint}</span>;
    }
    return (
      <>
        Adding <b>Fake Click</b> Sequence #{ordinal}
      </>
    );
  }

  if (change?.hint) {
    return <span>{change.hint}</span>;
  }

  return <span>{change.editorSelector}</span>;
}
